import { BadgeCheck, Activity, User } from 'lucide-react'
import type { LucideIcon } from 'lucide-react'

import { ROUTES } from 'routes/routes'
import { shouldSlide, tabSlideDirection } from 'utils/pageTransition'
import type { SlideDirection } from 'utils/pageTransition'

/**
 * The app's primary destinations, and the rules about them — ONE source, read by
 * BOTH presentations of the nav.
 *
 * This file exists because of the bug it fixes. The destination list lived inside
 * `BottomNav.tsx`, which is `display: { xs: 'flex', md: 'none' }`, so DESKTOP had
 * no access to it at all: it navigated from a hamburger on the certificates page
 * holding a different, hand-maintained set (Transfers, Profile, Notifications,
 * Help center, Log out). The two lists disagreed, and the disagreement was not a
 * detail — Certificates and Activity, the app's two primary destinations, were
 * simply unreachable at desktop width, while two of the menu's five entries went
 * nowhere at all.
 *
 * So the rule is: a presentation of the nav may decide HOW it looks (a bottom bar
 * on a phone, a left rail on a desktop) but never WHAT it contains. Anything that
 * answers "which destinations exist", "which route lights which one" or "does the
 * nav show here" belongs in this file. `BottomNav` and `SideNav` are then two
 * renderings of one list and cannot drift again.
 */

export interface PrimaryNavItem {
  label: string
  icon: LucideIcon
  to: string
  /** Routes that should also light this item (a detail page under the section). */
  match: (path: string) => boolean
}

/**
 * THREE destinations, not four.
 *
 * `Transfers` was dropped on purpose rather than shipped pointing at nothing:
 * it is a stub the certificates page itself marks as "owned by the Transfer
 * epic", so an entry for it would either dead-end or pre-empt that epic's design.
 * Three is a normal bar size; it can be added when the destination is real.
 */
export const PRIMARY_NAV_ITEMS: PrimaryNavItem[] = [
  {
    label: 'Certificates',
    // VERA-315: `BadgeCheckIcon`, the same glyph the platform's sidebar uses for
    // Certificates. A brand operator sees both apps, and the app was showing a
    // generic 4-square grid — which reads as "all items" or a layout switcher,
    // not as a certificate.
    icon: BadgeCheck,
    to: ROUTES.CERTIFICATES,
    // Also lights on a certificate detail / share / access page, which are
    // children of this section rather than destinations of their own.
    match: p => p.startsWith('/certificates'),
  },
  {
    label: 'Activity',
    icon: Activity,
    // VERA-315: points at the real cross-certificate feed. It used to open
    // `/notifications` — the notification PREFERENCES page — so the item's name
    // and its destination disagreed. Those settings now live under Profile.
    to: ROUTES.ACTIVITY,
    match: p => p.startsWith('/activity'),
  },
  {
    label: 'Profile',
    icon: User,
    // `/notifications` lights PROFILE now: it is reached from a Profile row and
    // is a settings page, so the destination that owns it is the one it sits
    // under.
    match: p => p.startsWith('/profile') || p.startsWith('/notifications'),
    to: ROUTES.PROFILE,
  },
]

/**
 * Router state for a navigation BETWEEN destinations — the direction the incoming
 * page travels, which `layouts/Default.tsx` reads and plays.
 *
 * Exported because the nav is not the only thing that moves between tabs.
 * Profile's "My certificates" back-link goes to the Certificates TAB, and
 * without this state it navigated bare, so the page it returned to hard-cut
 * into place while the identical move made from the bar two rows below it
 * slid. Same destination, same direction, two different animations.
 *
 * Sending the state rather than inventing a back-specific transition is the
 * point: "back" here IS a tab switch, and `tabSlideDirection` already answers
 * which way it travels — Profile is rightmost, Certificates leftmost, so the
 * list comes in from the LEFT, exactly as tapping the tab does.
 *
 * `undefined` (not `{ tabSlide: false }`) when it should not animate, so
 * `location.state` stays absent for the cold-load case the flag protects — the
 * same contract `pushState()` keeps for pushed pages.
 *
 * On DESKTOP this always returns `undefined`, because `shouldSlide()` is gated on
 * `innerWidth < 900`. The rail can therefore call it unconditionally: a horizontal
 * page slide is a phone metaphor, and it stays one.
 */
export const tabNavState = (
  fromPath: string,
  to: string,
): { tabSlide: SlideDirection } | undefined => {
  const fromIndex = PRIMARY_NAV_ITEMS.findIndex(item => item.match(fromPath))
  const toIndex = PRIMARY_NAV_ITEMS.findIndex(item => item.to === to)
  if (!shouldSlide() || fromIndex === -1 || toIndex === -1 || fromIndex === toIndex) {
    return undefined
  }
  return { tabSlide: tabSlideDirection(fromIndex, toIndex) }
}

/**
 * Whether the MOBILE BAR renders on this route.
 *
 * ⚠️ This is the PHONE's rule only. The desktop rail asks a different question —
 * `showsDesktopRail` below — and the two deliberately disagree. Read that one's
 * note before assuming a route that hides the bar also hides the rail.
 *
 * The bar hides where something else OWNS THE BOTTOM EDGE, or the surface is
 * immersive. It is not "top-level destinations only" — that was the original
 * rule and it was too blunt.
 *
 * A certificate and its share screen run a full-bleed hero and the share screen
 * claims the bottom for its own `StickyActionBar`; the scans map runs to the
 * frame. Keeping the bar there would put two competing bars on one screen, which
 * is what Artsy's artwork detail and Chrono24's listing detail also avoid.
 *
 * A pushed SETTINGS page has neither problem, so Notifications keeps the bar —
 * matching the more common mobile convention, where a tab bar persists through
 * its tab's navigation stack and disappears only for immersive content.
 *
 * ⚠️ ONE CLAUSE OF THAT RATIONALE IS NOW STALE, and it is left standing on
 * purpose rather than quietly acted on. The certificate PAGE no longer has a
 * `StickyActionBar` — the pinned bar was removed and its actions returned to
 * their own blocks, so `pages/SharePage` is the only file that renders one
 * (grep it). The certificate page's remaining reason to hide the bar is the
 * full-bleed hero, which is real on a phone. Whether that alone still earns the
 * hide is a MOBILE design question, and nothing here has changed it: this
 * predicate's behaviour at `xs` is byte-for-byte what it was.
 */
export const showsPrimaryNav = (pathname: string): boolean =>
  pathname === ROUTES.CERTIFICATES ||
  pathname === ROUTES.ACTIVITY ||
  pathname === ROUTES.PROFILE ||
  /*
   * Notifications keeps the nav even though it is a PUSHED page.
   *
   * The rule above is not "top-level destinations only" any more; it is "hide
   * the nav where something else owns the bottom edge, or the surface is
   * immersive". A certificate claims the bottom for its own StickyActionBar and
   * runs a full-bleed hero; the scans map runs to the frame. Notifications does
   * neither — it is a settings list inside Profile's stack, and hiding the nav
   * there costs a trip back to Profile to change destination and buys nothing.
   *
   * This is also the more common mobile convention: a tab bar persists through
   * a tab's navigation stack and disappears only for immersive content.
   */
  pathname === ROUTES.NOTIFICATIONS

/**
 * Whether the DESKTOP RAIL renders — a different question from the bar's, and
 * this is the file's one deliberate divergence between the two presentations.
 *
 * ## Why it is not `showsPrimaryNav`
 *
 * It was, and that was the bug. The bar's hide list is derived from a PHONE
 * constraint — something else owns the bottom edge, or the surface is immersive —
 * and the rail has neither problem: it is a left column, it owns no bottom edge,
 * and it does not overlay a hero. Sharing the predicate applied a phone's reason
 * to a desktop, and the visible result was that the app's persistent navigation
 * DISAPPEARED on every certificate detail page and sub-view. Navigation that
 * vanishes on detail pages is the defect; the misalignment below was its symptom.
 *
 * The symptom, measured at a 1280 viewport before this change: the rail is in
 * FLOW (`layouts/Default`), so a page that renders it gets `100vw - 200` and a
 * page that hides it gets the whole `100vw`. The certificates list ran 248 → 1232
 * and a certificate ran 104 → 1176 — the same app, one tap apart, 144px out of
 * step, and no container-width constant could have reconciled them because the
 * two pages were centring inside different boxes. With the rail on both, both
 * centre inside the same 1080 and the columns line up by construction rather than
 * by arithmetic somebody has to maintain.
 *
 * ## The rule: the rail shows wherever a nav item would LIGHT UP
 *
 * Reusing `PRIMARY_NAV_ITEMS`' own `match` functions rather than listing routes
 * again is the point — it is the same "one source" contract the rest of this file
 * keeps. If the nav can tell you where you are, the nav should be on screen. So a
 * certificate, its Provenance / Scans / Activity sub-views, its Share screen and
 * `/profile/code` all keep the rail, because Certificates or Profile lights on
 * each of them.
 *
 * ## `isAuthenticated` is NOT optional — a route match alone is wrong
 *
 * `LayoutDefault` wraps the unauthenticated routes too (sign-in, signup, claim),
 * so the original docblock was right that "every layout route" would put a
 * signed-in app's navigation on a sign-in page. Matching a nav item narrows that,
 * but NOT far enough, and the gap is the case this app exists for:
 * `ROUTES.CERTIFICATE` (`/certificates/:id`) is
 * registered OUTSIDE `<ProtectedRoute>` in `App.tsx` — a certificate is PUBLIC,
 * because it is reached by a stranger tapping an NFC tag. `startsWith('/certificates')`
 * matches for that stranger, so without the auth check they would be shown
 * Certificates / Activity / Profile / Log out for an account they do not have.
 *
 * Callers pass it from `useAuth()`; it is a parameter rather than a hook call in
 * here so this stays a pure predicate that can be reasoned about and tested.
 */
export const showsDesktopRail = (pathname: string, isAuthenticated: boolean): boolean =>
  isAuthenticated && PRIMARY_NAV_ITEMS.some(item => item.match(pathname))
